import React, { createContext, useContext, useReducer, useRef } from 'react';
import type { CompanyAnnualSelectItem } from '@/services/accountService/companyAnnualService';
import type { CompanyInfoImportData, GlColumnDef, GlColumnMetaVO, UnmatchedColumn } from '@/services/accountService/glInvoiceService';
import type { MappingRow } from '@/pages/components/account/gl-invoice/create-stepper/StepCCateMapping';
import type { MappingConfirmSummary } from '@/utils/glColumnMappingHelpers';
import { buildColumnKey, fetchGlImportCache, fetchColumnDefs } from '@/services/accountService/glInvoiceService';
import { originalKeyFromRateKey, stripDupSuffix } from '@/pages/components/account/gl-invoice/mode/tabs/bankColumnUtils';
import type { CompanyCrValidateHandle } from '@/pages/components/company-info/company/useCompanyCrGovLookup';

export interface DrCrItem {
    columnKey: string | null;
    amount: string;
    /** 空白项归属 DR/CR；已指定 side 时以 side 为准 */
    side?: 'dr' | 'cr';
    /** 外币原币行的汇率，保存到 {glCode}-RATE */
    exRate?: string;
}

export interface InvoiceEntryRow {
    key: string;
    date: string;
    description: string;
    voucherNo: string;
    invoiceNo: string;
    invoiceDate: string;
    chequeNo: string;
    details: string;
    price: string;
    drCrItems: DrCrItem[];
    invoiceId?: string;
    /** Phase 2 方案D: 後端行級校驗警告 */
    validationWarnings?: string[];
}

export type PreloadStatus = 'idle' | 'loading' | 'done' | 'error';

export type GlInvoiceCreateState = {
    selectedCompany: any | null;
    companyId: number | null;
    selectedAnnual: CompanyAnnualSelectItem | null;
    annualId: number | null;
    isCreatingAnnual: boolean;
    isProcessing: boolean;
    beforeNext?: () => Promise<boolean>;
    contentData: CompanyInfoImportData | null;
    importedCompany: any | null;
    importFlags: {
        companyMatched: boolean;
        companyNameUpdated: boolean;
    } | null;
    importToken: string | null;
    /** 旧格式标志 */
    legacyFormat: boolean;
    /** 全量 Column Mapping（已匹配 + 未匹配） */
    columnMappings: UnmatchedColumn[] | null;
    /** 兼容：与 columnMappings 同源 */
    unmatchedColumns: UnmatchedColumn[] | null;
    /** 列映射待确认（InvoiceEntry 数据加载需等待此标志变 false） */
    pendingLegacyMapping: boolean;
    /** 映射步骤中用户填写的 MappingRow（回退时保留状态） */
    mappingRows: MappingRow[] | null;
    // Phase 1: 預加載字段
    preloadedColumnDefs: GlColumnDef[] | null;
    /** 导入解析后的列元数据（含 Excel Row 8 标题），保存时传给后端 */
    preloadedColumnMetaMap: Record<string, GlColumnMetaVO> | null;
    preloadedGlData: {
        rows: InvoiceEntryRow[];
        summaryPriorYear: Record<string, number>;
    } | null;
    preloadStatus: PreloadStatus;
    preloadError: string | null;
    /** 预加载的 GL 叶子节点分类（旧格式上传时并行预加载，StepCCateMapping 优先使用） */
    preloadedGlCategories: any[] | null;
    /** Stepper 步骤阻塞错误（如 Annual 校验失败），显示在对话框顶部 */
    stepError: string | null;
    /** Column Mapping 确认后的摘要（列数/行数/有金额行数） */
    mappingSummary: MappingConfirmSummary | null;
    /** 上年结转（GL 第 10 行原值，key 为 fsCode|glCode 或 fsCode|LY） */
    summaryPriorYear: Record<string, number>;
    /** 上年 AI 合并 remark：fsCode → 文案 */
    summaryPriorYearRemarks: Record<string, string>;
};

const initialState: GlInvoiceCreateState = {
    selectedCompany: null,
    companyId: null,
    selectedAnnual: null,
    annualId: null,
    isCreatingAnnual: false,
    isProcessing: false,
    beforeNext: undefined,
    contentData: null,
    importedCompany: null,
    importFlags: null,
    importToken: null,
    legacyFormat: false,
    columnMappings: null,
    unmatchedColumns: null,
    pendingLegacyMapping: false,
    mappingRows: null,
    preloadedColumnDefs: null,
    preloadedColumnMetaMap: null,
    preloadedGlData: null,
    preloadStatus: 'idle',
    preloadError: null,
    preloadedGlCategories: null,
    stepError: null,
    mappingSummary: null,
    summaryPriorYear: {},
    summaryPriorYearRemarks: {},
};

export type GlInvoiceCreateAction =
    | { type: 'set_company'; payload: any | null }
    | { type: 'set_annual'; payload: CompanyAnnualSelectItem | null }
    | { type: 'set_annual_id'; payload: number | null }
    | { type: 'set_is_creating_annual'; payload: boolean }
    | { type: 'set_is_processing'; payload: boolean }
    | { type: 'set_before_next'; payload: GlInvoiceCreateState['beforeNext'] }
    | { type: 'set_content_data'; payload: CompanyInfoImportData | null }
    | { type: 'set_imported_company'; payload: any | null }
    | { type: 'set_import_flags'; payload: { companyMatched: boolean; companyNameUpdated: boolean } }
    | { type: 'set_import_token'; payload: string | null }
    | { type: 'set_legacy_format'; payload: boolean }
    | { type: 'set_column_mappings'; payload: UnmatchedColumn[] | null }
    | { type: 'set_unmatched_columns'; payload: UnmatchedColumn[] | null }
    | { type: 'set_pending_legacy_mapping'; payload: boolean }
    | { type: 'set_mapping_rows'; payload: MappingRow[] | null }
    | { type: 'set_preload_status'; payload: PreloadStatus }
    | { type: 'set_preload_error'; payload: string | null }
    | { type: 'set_preloaded_column_defs'; payload: GlColumnDef[] }
    | { type: 'set_preloaded_column_meta_map'; payload: Record<string, GlColumnMetaVO> | null }
    | { type: 'set_preloaded_gl_data'; payload: { rows: InvoiceEntryRow[]; summaryPriorYear: Record<string, number> } }
    | { type: 'set_preloaded_gl_categories'; payload: any[] | null }
    | { type: 'set_step_error'; payload: string | null }
    | { type: 'set_mapping_summary'; payload: MappingConfirmSummary | null }
    | { type: 'set_summary_prior_year'; payload: Record<string, number> }
    | { type: 'set_summary_prior_year_remarks'; payload: Record<string, string> }
    | { type: 'clear' };

function reducer(state: GlInvoiceCreateState, action: GlInvoiceCreateAction): GlInvoiceCreateState {
    switch (action.type) {
        case 'set_company': {
            const nextCompanyId = action.payload
                ? Number((action.payload as any).companyId ?? (action.payload as any).id)
                : null;
            const companyChanged = state.companyId !== nextCompanyId;
            // 换公司时清掉按公司套过别名的 preload，避免新公司读到上家 DR/CR / 把上家 meta 写入 Co.GL
            // 保留 importToken + columnMetaMap（来自本次 Excel，与公司无关），由 Stepper 按新公司重新 preload
            if (!companyChanged) {
                return {
                    ...state,
                    selectedCompany: action.payload,
                    companyId: nextCompanyId,
                    selectedAnnual: null,
                    annualId: null,
                    isCreatingAnnual: false,
                };
            }
            return {
                ...state,
                selectedCompany: action.payload,
                companyId: nextCompanyId,
                selectedAnnual: null,
                annualId: null,
                isCreatingAnnual: false,
                preloadedColumnDefs: null,
                preloadedGlData: null,
                preloadStatus: 'idle',
                preloadError: null,
                // 已有本次导入 token / 未匹配列 / contentData 时保留，避免导入后 set_company 清掉 Column Mapping
                ...(state.importToken || state.columnMappings?.length || state.unmatchedColumns?.length || state.contentData
                    ? {}
                    : {
                        importToken: null,
                        preloadedColumnMetaMap: null,
                        summaryPriorYear: {},
                        legacyFormat: false,
                        columnMappings: null,
                        unmatchedColumns: null,
                        pendingLegacyMapping: false,
                        mappingRows: null,
                        mappingSummary: null,
                        contentData: null,
                        importedCompany: null,
                        importFlags: null,
                    }),
            };
        }
        case 'set_annual':
            return {
                ...state,
                selectedAnnual: action.payload,
                annualId: action.payload ? Number(action.payload.annualId) : null,
                isCreatingAnnual: false,
            };
        case 'set_annual_id':
            return { ...state, annualId: action.payload };
        case 'set_is_creating_annual':
            return { ...state, isCreatingAnnual: action.payload };
        case 'set_is_processing':
            return { ...state, isProcessing: action.payload };
        case 'set_before_next':
            return { ...state, beforeNext: action.payload };
        case 'set_content_data':
            return { ...state, contentData: action.payload };
        case 'set_imported_company':
            return { ...state, importedCompany: action.payload };
        case 'set_import_flags':
            return { ...state, importFlags: action.payload };
        case 'set_import_token':
            // 新 token 时重置 preload，避免沿用上一份导入的 columnDefs / 行数据
            return {
                ...state,
                importToken: action.payload,
                ...(action.payload && action.payload !== state.importToken
                    ? {
                        preloadedColumnDefs: null,
                        preloadedGlData: null,
                        preloadedColumnMetaMap: null,
                        preloadStatus: 'idle' as PreloadStatus,
                        preloadError: null,
                    }
                    : {}),
            };
        case 'set_legacy_format':
            return { ...state, legacyFormat: action.payload };
        case 'set_column_mappings':
            return { ...state, columnMappings: action.payload };
        case 'set_unmatched_columns':
            return { ...state, unmatchedColumns: action.payload };
        case 'set_pending_legacy_mapping':
            return { ...state, pendingLegacyMapping: action.payload };
        case 'set_mapping_rows':
            return { ...state, mappingRows: action.payload };
        case 'set_preload_status':
            return { ...state, preloadStatus: action.payload };
        case 'set_preload_error':
            return { ...state, preloadError: action.payload };
        case 'set_preloaded_column_defs':
            return { ...state, preloadedColumnDefs: action.payload };
        case 'set_preloaded_column_meta_map':
            return { ...state, preloadedColumnMetaMap: action.payload };
        case 'set_preloaded_gl_data': {
            const incomingPrior = action.payload.summaryPriorYear ?? {};
            const existingPrior = state.summaryPriorYear;
            // Mapping 确认前上年 key 多为 _UNM_ 占位；确认/重拉后必须用新 key，
            // 否则 Last Year 按 fsCode 汇总时全是 0。
            const existingIsPlaceholder = Object.keys(existingPrior).some((k) => k.includes('_UNM_'));
            const summaryPriorYear =
                Object.keys(existingPrior).length === 0 || existingIsPlaceholder
                    ? incomingPrior
                    : existingPrior;
            return {
                ...state,
                preloadedGlData: action.payload,
                summaryPriorYear,
            };
        }
        case 'set_summary_prior_year':
            return { ...state, summaryPriorYear: action.payload };
        case 'set_summary_prior_year_remarks':
            return { ...state, summaryPriorYearRemarks: action.payload };
        case 'set_preloaded_gl_categories':
            return { ...state, preloadedGlCategories: action.payload };
        case 'set_step_error':
            return { ...state, stepError: action.payload };
        case 'set_mapping_summary':
            return { ...state, mappingSummary: action.payload };
        case 'clear':
            return { ...initialState };
        default:
            return state;
    }
}

const GlInvoiceCreateContext = createContext<{
    state: GlInvoiceCreateState;
    dispatch: React.Dispatch<GlInvoiceCreateAction>;
    /** Step1 Excel Validate → CompanyDetail CR 校验 */
    crValidateRef: React.MutableRefObject<CompanyCrValidateHandle | null>;
} | null>(null);

export function GlInvoiceCreateProvider({ children }: { children: React.ReactNode }) {
    const [state, dispatch] = useReducer(reducer, initialState);
    const crValidateRef = useRef<CompanyCrValidateHandle | null>(null);
    return (
        <GlInvoiceCreateContext.Provider value={{ state, dispatch, crValidateRef }}>
            {children}
        </GlInvoiceCreateContext.Provider>
    );
}

export function useGlInvoiceCreateCtx() {
    const ctx = useContext(GlInvoiceCreateContext);
    if (!ctx) throw new Error('useGlInvoiceCreateCtx must be used within GlInvoiceCreateProvider');
    return ctx;
}

// ===================== 共用工具函數 =====================

/**
 * 按 UI 列归属拆分 DR/CR。
 * 有明确 side 时以 side 为准（避免在 CR 列填正数金额被挪到 DR）；
 * side 未设时才按金额正负回退（导入旧数据）。
 */
export function splitDrCrSides(items: DrCrItem[]): { dr: number[]; cr: number[] } {
    const dr: number[] = [];
    const cr: number[] = [];
    items.forEach((item, idx) => {
        if (item.side === 'cr') {
            cr.push(idx);
            return;
        }
        if (item.side === 'dr') {
            dr.push(idx);
            return;
        }
        const raw = (item.amount ?? '').trim();
        const n = parseFloat(raw);
        if (raw && !Number.isNaN(n) && n < 0) cr.push(idx);
        else dr.push(idx);
    });
    return { dr, cr };
}

export function makeEmptyDrCrItem(side: 'dr' | 'cr'): DrCrItem {
    return { columnKey: null, amount: '', side };
}

export function makeEmptyRow(): InvoiceEntryRow {
    return {
        key: `row-${Date.now()}-${Math.random()}`,
        date: '',
        description: '',
        voucherNo: '',
        invoiceNo: '',
        invoiceDate: '',
        chequeNo: '',
        details: '',
        price: '',
        drCrItems: [
            makeEmptyDrCrItem('dr'),
            makeEmptyDrCrItem('cr'),
        ],
    };
}

// ===================== 列定義索引（優化 resolveColumnDef 查找性能） =====================

/**
 * 預構建的列定義索引，供 resolveColumnDef 使用。
 * 將原先每次 O(D) 的線性查找優化為 O(1) 的 Map 查找。
 */
export interface ColumnDefIndex {
    byCanonicalKey: Map<string, GlColumnDef>;
    byFsAndGlCode: Map<string, GlColumnDef>;
    byFsOnly: Map<string, GlColumnDef[]>;
}

export function buildColumnDefIndex(columnDefs: GlColumnDef[]): ColumnDefIndex {
    const byCanonicalKey = new Map<string, GlColumnDef>();
    const byFsAndGlCode = new Map<string, GlColumnDef>();
    const byFsOnly = new Map<string, GlColumnDef[]>();
    for (const def of columnDefs) {
        byCanonicalKey.set(buildColumnKey(def), def);
        if (def.glCode) byFsAndGlCode.set(`${def.fsCode}:${def.glCode}`, def);
        const arr = byFsOnly.get(def.fsCode) ?? [];
        arr.push(def);
        byFsOnly.set(def.fsCode, arr);
    }
    return { byCanonicalKey, byFsAndGlCode, byFsOnly };
}

/**
 * 將後端 columnValues 的原始 key 解析到對應的 GlColumnDef。
 *
 * 背景：後端 GL Sheet 解析時，若 Row 5 glCode 缺失且 DB (fsCode+nameEn) 查不到，
 * 會回退為 glCode = fsCode，產生的 columnValues key 形如 "fsCode|fsCode"；
 * 但 /column-defs 接口在同樣情況下 glCode = null，前端 buildColumnKey 生成
 * 的是 "fsCode|"，兩者不匹配導致 UI Autocomplete 無法命中對應的列定義。
 *
 * 此解析器按如下順序嘗試（均為 O(1) Map 查找）：
 * 1) 直接用 buildColumnKey(def) 的規範形式精確匹配
 * 2) 回退匹配：rawKey 拆成 "fs|id"，用 "fs:id" 在 byFsAndGlCode 中查找
 * 3) fsCode fallback：glCode 為空且 id === fs 的定義
 * 4) DUP 消歧義：strip "-DUP{N}" 後綴，用 base glCode 匹配
 */
function resolveColumnDef(rawKey: string, index: ColumnDefIndex): GlColumnDef | null {
    // 1) 直接匹配前端規範形式
    const direct = index.byCanonicalKey.get(rawKey);
    if (direct) return direct;
    // 2) 回退匹配後端 fsCode 代替 glCode 形式
    const sepIdx = rawKey.indexOf('|');
    if (sepIdx < 0) return null;
    const fs = rawKey.slice(0, sepIdx);
    const id = rawKey.slice(sepIdx + 1);
    const byFsGl = index.byFsAndGlCode.get(`${fs}:${id}`);
    if (byFsGl) return byFsGl;
    // 3) fsCode fallback: glCode 為空時 id === fs
    const fsDefs = index.byFsOnly.get(fs);
    if (fsDefs) {
        for (const def of fsDefs) {
            if ((!def.glCode || def.glCode === '') && id === fs) return def;
        }
    }
    // 4) 旧格式 DUP 消歧義回退：strip "-DUP{N}" 後綴，用 base glCode 匹配
    const dupMatch = id.match(/^(.+)-DUP\d+$/);
    if (dupMatch) {
        const baseGlCode = dupMatch[1];
        return index.byFsAndGlCode.get(`${fs}:${baseGlCode}`) ?? null;
    }
    return null;
}

/**
 * 判斷一個 columnValues key 是否是 Ex-rate（外幣匯率）列。
 * 後端 resolveItemCodeMap 會給匯率列的 glCode 加上 "-RATE" 後綴，
 * 對應 key 形如 "fsCode|<origGlCode>-RATE" 或 "...-RATE-DUP{n}"。
 */
function baseColumnKey(key: string): string {
    const sep = key.indexOf('|');
    if (sep < 0) return key;
    return `${key.slice(0, sep)}|${stripDupSuffix(key.slice(sep + 1))}`;
}

function isExRateKey(key: string): boolean {
    const glCode = key.includes('|') ? key.slice(key.indexOf('|') + 1) : key;
    const base = glCode.replace(/-DUP\d+$/, '');
    return base.endsWith('-RATE');
}

export function rowFromGlSheet(r: any, columnDefs?: GlColumnDef[] | ColumnDefIndex): InvoiceEntryRow {
    const colVals: Record<string, number> = r.columnValues || {};
    const entries = Object.entries(colVals);

    // 構建帶解析元數據的臨時項：canonical key + columnIndex，用於後續排序與歸一
    type TmpItem = {
        columnKey: string | null;
        amount: string;
        colIdx: number;
    };
    // 支援 GlColumnDef[](向後相容) 或 ColumnDefIndex(批量優化)
    const index: ColumnDefIndex | null = !columnDefs
        ? null
        : Array.isArray(columnDefs)
            ? (columnDefs.length > 0 ? buildColumnDefIndex(columnDefs) : null)
            : columnDefs;
    const hasDefs = index !== null;
    // [DEBUG] 逐項追蹤解析結果，輔助排查 "amount 兩邊都有但 Position 只有單邊" 類問題
    const unresolvedKeys: string[] = [];
    const filteredExRateKeys: string[] = [];
    const exRateByOriginalKey = new Map<string, string>();
    const tmpItems: TmpItem[] = entries
        .filter(([k, v]) => {
            if (Number(v) === 0) return false;
            // Ex-rate 不单独成行，挂到对应原币科目的 exRate
            if (isExRateKey(k)) {
                filteredExRateKeys.push(k);
                const originalKey = originalKeyFromRateKey(k);
                if (originalKey) exRateByOriginalKey.set(originalKey, String(Math.abs(Number(v))));
                return false;
            }
            return true;
        })
        .map(([k, v]) => {
            const def = (hasDefs && index) ? resolveColumnDef(k, index) : null;
            if (hasDefs && !def) unresolvedKeys.push(k);
            return {
                // 歸一為前端 buildColumnKey 規範形式；未解析則保留原 key（保底兼容）
                columnKey: def ? buildColumnKey(def) : k,
                amount: String(v),
                colIdx: def?.columnIndex ?? Number.MAX_SAFE_INTEGER,
            };
        });

    // 按 Excel 物理列順序 (columnIndex) 升序排列，與模板列序一致；未解析的尾隨
    tmpItems.sort((a, b) => a.colIdx - b.colIdx);

    const drCrItems: DrCrItem[] = tmpItems.map(({ columnKey, amount }) => {
        const n = parseFloat(amount);
        const side: 'dr' | 'cr' | undefined =
            !Number.isNaN(n) && n !== 0 ? (n < 0 ? 'cr' : 'dr') : undefined;
        // UI 金额一律正数；CR 保存时再取负
        const displayAmount = !Number.isNaN(n) && n !== 0
            ? String(Math.abs(n))
            : amount;
        const baseKey = columnKey ? baseColumnKey(columnKey) : '';
        const exRate = baseKey ? exRateByOriginalKey.get(baseKey) : undefined;
        return { columnKey, amount: displayAmount, side, ...(exRate ? { exRate } : {}) };
    });
    // [DEBUG] GL import 數據丟失排查（僅開發環境輸出，避免生產環境大量 console 影響性能）
    if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log('[GL-DEBUG] rowFromGlSheet', {
            invoiceId: r.invoiceId,
            voucherNo: r.voucherNo,
            rawEntryCount: entries.length,
            columnValues: colVals,
            parsedDrCrCount: drCrItems.length,
            resolvedWithDefs: hasDefs,
            filteredExRateKeys,
            unresolvedKeys,
        });
    }
    const sides = splitDrCrSides(drCrItems);
    if (sides.dr.length === 0) drCrItems.push(makeEmptyDrCrItem('dr'));
    if (sides.cr.length === 0) drCrItems.push(makeEmptyDrCrItem('cr'));
    return {
        key: `row-${r.invoiceId || Date.now()}-${Math.random()}`,
        invoiceId: r.invoiceId,
        date: r.date || '',
        description: r.description || '',
        voucherNo: r.voucherNo || '',
        invoiceNo: r.invoiceNo || '',
        invoiceDate: r.invoiceDate || '',
        chequeNo: r.chequeNo || '',
        details: r.details || '',
        price: r.price != null ? String(r.price) : '',
        drCrItems,
        validationWarnings: r.validationWarnings || undefined,
    };
}

// ===================== 客戶端重映射工具 =====================

/**
 * 客戶端重映射預加載數據的 drCrItems key。
 * 在用戶確認映射後調用，將舊 key（未匹配時的佔位 key）替換為新的規範 key。
 *
 * @param preloadedData 已預加載的 GL 數據
 * @param keyMapping    後端返回或前端構建的 oldKey → newKey 映射表
 * @returns 重映射後的數據副本（不修改原數據）
 */
export function remapPreloadedData(
    preloadedData: { rows: InvoiceEntryRow[]; summaryPriorYear: Record<string, number> },
    keyMapping: Record<string, string>,
): { rows: InvoiceEntryRow[]; summaryPriorYear: Record<string, number> } {
    const remappedRows = preloadedData.rows.map(row => ({
        ...row,
        drCrItems: row.drCrItems.map(item => {
            if (item.columnKey && keyMapping[item.columnKey]) {
                return { ...item, columnKey: keyMapping[item.columnKey] };
            }
            return item;
        }),
    }));
    // summaryPriorYear 的 key 也可能需要重映射
    const remappedSummary: Record<string, number> = {};
    for (const [k, v] of Object.entries(preloadedData.summaryPriorYear)) {
        remappedSummary[keyMapping[k] ?? k] = v;
    }
    return { rows: remappedRows, summaryPriorYear: remappedSummary };
}

/** Column Mapping 确认后：把预加载 columnMetaMap 的 _UNM_ key 换成正式 fs|gl */
export function remapColumnMetaMap(
    metaMap: Record<string, GlColumnMetaVO> | null | undefined,
    keyMapping: Record<string, string>,
): Record<string, GlColumnMetaVO> | null {
    if (!metaMap || Object.keys(metaMap).length === 0) return metaMap ?? null;
    const out: Record<string, GlColumnMetaVO> = {};
    for (const [oldKey, meta] of Object.entries(metaMap)) {
        const newKey = keyMapping[oldKey] ?? oldKey;
        if (!meta) {
            out[newKey] = meta;
            continue;
        }
        const pipe = newKey.indexOf('|');
        const fsCode = pipe > 0 ? newKey.slice(0, pipe) : (meta.fsCode ?? '');
        const glCode = pipe > 0 ? newKey.slice(pipe + 1) : (meta.glCode ?? '');
        out[newKey] = {
            ...meta,
            fsCode,
            glCode,
        };
    }
    return out;
}

// ===================== 共享預加載工廠函數 =====================

/**
 * 創建 startPreload 函數，用於後台預加載 GL 數據 + 列定義。
 * 在 GlInvoiceCreateStepper 和 StepCCateMapping 中共用，避免代碼重複。
 *
 * 使用方式：
 *   const startPreload = useMemo(() => createStartPreload(dispatch), [dispatch]);
 */
export function createStartPreload(dispatch: React.Dispatch<GlInvoiceCreateAction>) {
    return async (token: string, companyId?: number | null) => {
        dispatch({ type: 'set_preload_status', payload: 'loading' });
        dispatch({ type: 'set_preload_error', payload: null });
        try {
            // 先請求 GL 緩存（方案B: 後端已附帶 columnDefs），傳入 companyId 用於別名替換
            const cached = await fetchGlImportCache(token, companyId ?? undefined);

            // 優先使用 cache 附帶的 columnDefs，減少一次獨立 API 請求
            let colDefsForRows = cached?.columnDefs ?? null;
            if (colDefsForRows && colDefsForRows.length > 0) {
                dispatch({ type: 'set_preloaded_column_defs', payload: colDefsForRows });
            } else {
                // fallback: 獨立請求列定義
                colDefsForRows = await fetchColumnDefs();
                dispatch({ type: 'set_preloaded_column_defs', payload: colDefsForRows });
            }

            if (cached?.columnMetaMap && Object.keys(cached.columnMetaMap).length > 0) {
                dispatch({ type: 'set_preloaded_column_meta_map', payload: cached.columnMetaMap });
            }

            if (cached?.list?.length) {
                // 分批轉換，避免大量數據阻塞主線程
                const BATCH_SIZE = 200;
                const rawList = cached.list;
                // 預構建索引一次，所有行共用（避免每行重建 Map）
                const defsIndex = colDefsForRows ? buildColumnDefIndex(colDefsForRows) : undefined;
                let rows: ReturnType<typeof rowFromGlSheet>[];
                if (rawList.length <= BATCH_SIZE) {
                    rows = rawList.map((r) => rowFromGlSheet(r, defsIndex));
                } else {
                    rows = [];
                    for (let i = 0; i < rawList.length; i += BATCH_SIZE) {
                        const batch = rawList.slice(i, i + BATCH_SIZE).map((r) => rowFromGlSheet(r, defsIndex));
                        rows.push(...batch);
                        // 讓出主線程一幀，避免 UI 卡頓
                        if (i + BATCH_SIZE < rawList.length) {
                            await new Promise<void>((resolve) =>
                                typeof requestIdleCallback === 'function'
                                    ? requestIdleCallback(() => resolve())
                                    : setTimeout(resolve, 0),
                            );
                        }
                    }
                }
                dispatch({
                    type: 'set_preloaded_gl_data',
                    payload: {
                        rows,
                        summaryPriorYear: cached.summaryPriorYear ?? {},
                    },
                });
            }

            dispatch({ type: 'set_preload_status', payload: 'done' });
        } catch (e: any) {
            dispatch({ type: 'set_preload_error', payload: e?.message || 'Preload failed' });
            dispatch({ type: 'set_preload_status', payload: 'error' });
        }
    };
}
